import {
  createSlug,
  subscribeToBusinessType,
  subscribeToBusinessTypesList,
  type BusinessType,
} from '@/services/firebase/firestore';
import type { Timestamp, Unsubscribe } from 'firebase/firestore';
import { useEffect, useRef, useState } from 'react';

interface BusinessTypeRaw {
  name?: string;
  slug?: string;
  output?: number;
  ts?: Timestamp;
}

export interface UseBusinessTypesResult {
  businessTypes: BusinessType[];
  loading: boolean;
  error: string | null;
  selectedBusinessType: BusinessType | null;
  selectedBusinessTypeLoading: boolean;
  setSelectedBusinessType: (businessType: BusinessType | null) => void;
  refetch: () => void;
}

export const useBusinessTypes = (): UseBusinessTypesResult => {
  const [businessTypes, setBusinessTypes] = useState<BusinessType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedBusinessType, setSelectedBusinessTypeState] = useState<BusinessType | null>(null);
  const [selectedBusinessTypeLoading, setSelectedBusinessTypeLoading] = useState(false);
  const unsubscribeRef = useRef<Unsubscribe | null>(null);
  const selectedBusinessTypeUnsubscribeRef = useRef<Unsubscribe | null>(null);
  const selectedBusinessTypeRef = useRef<BusinessType | null>(null);

  // Keep ref in sync with state
  useEffect(() => {
    selectedBusinessTypeRef.current = selectedBusinessType;
  }, [selectedBusinessType]);

  const refetch = () => {
    setLoading(true);
    setError(null);
  };

  // Custom setSelectedBusinessType that subscribes to real-time updates
  const setSelectedBusinessType = (businessType: BusinessType | null) => {
    // Clean up previous subscription if exists
    if (selectedBusinessTypeUnsubscribeRef.current) {
      selectedBusinessTypeUnsubscribeRef.current();
      selectedBusinessTypeUnsubscribeRef.current = null;
    }

    if (!businessType) {
      setSelectedBusinessTypeState(null);
      return;
    }

    // Set the basic business type immediately
    setSelectedBusinessTypeState(businessType);
    setSelectedBusinessTypeLoading(true);

    // Set up real-time subscription to enhance with additional details (history, requests, metadata)
    selectedBusinessTypeUnsubscribeRef.current = subscribeToBusinessType(
      businessType.slug,
      (updatedBusinessType) => {
        if (updatedBusinessType) {
          // Sort history by timestamp descending (most recent first)
          const sortedHistory = (updatedBusinessType.history || []).sort((a, b) => {
            const aTime = a.ts?.toDate ? a.ts.toDate().getTime() : 0;
            const bTime = b.ts?.toDate ? b.ts.toDate().getTime() : 0;
            return bTime - aTime;
          });

          // Requests don't have timestamps, so just use as-is
          const requests = updatedBusinessType.requests || [];

          // Merge the subscription data with the basic business type
          setSelectedBusinessTypeState({
            ...businessType, // Keep basic info from list
            history: sortedHistory,
            requests: requests,
            metadata: updatedBusinessType.metadata || {
              industry_painpoints: [],
              website_objectives: [],
            },
            output: updatedBusinessType.output || businessType.output,
            default_sequence_id: updatedBusinessType.default_sequence_id,
          });
        } else {
          // Keep the basic business type if document doesn't exist
          setSelectedBusinessTypeState({
            ...businessType,
            history: [],
            requests: [],
            metadata: { industry_painpoints: [], website_objectives: [] },
          });
        }
        setSelectedBusinessTypeLoading(false);
      },
      (error) => {
        console.error('Error subscribing to selected business type:', error);
        // Keep the basic business type with empty additional details on error
        setSelectedBusinessTypeState({
          ...businessType,
          history: [],
          requests: [],
          metadata: { industry_painpoints: [], website_objectives: [] },
        });
        setSelectedBusinessTypeLoading(false);
      }
    );
  };

  useEffect(() => {
    const handleBusinessTypesListUpdate = (businessTypesList: (BusinessTypeRaw | string)[]) => {
      // Transform array data to BusinessType format (basic list items)
      const businessTypes: BusinessType[] = businessTypesList.map((bt, index: number) => {
        if (typeof bt === 'string') {
          return {
            id: createSlug(bt),
            name: bt,
            slug: createSlug(bt),
            output: 0,
            ts: { toDate: () => new Date() } as Timestamp,
          };
        }
        return {
          id: bt.slug || `business-type-${index}`,
          name: bt.name || '',
          slug: bt.slug || '',
          output: bt.output || 0,
          ts: bt.ts || ({ toDate: () => new Date() } as Timestamp),
        };
      });

      // Sort alphabetically by name
      businessTypes.sort((a, b) => {
        return a.name.localeCompare(b.name);
      });

      setBusinessTypes(businessTypes);
      setLoading(false);
      setError(null);

      // Update selected business type if it exists in the new data
      const currentSelected = selectedBusinessTypeRef.current;
      if (currentSelected) {
        const updatedSelected = businessTypes.find((bt) => bt.id === currentSelected.id);
        if (updatedSelected) {
          // Only update the basic info, keep the full details if they exist
          setSelectedBusinessTypeState((prev) =>
            prev ? { ...prev, ...updatedSelected } : updatedSelected
          );
        } else {
          // Selected business type was deleted
          setSelectedBusinessTypeState(null);
        }
      }
    };

    const handleError = (err: Error) => {
      console.error('Business types subscription error:', err);
      setError(err.message || 'Failed to load business types');
      setLoading(false);
    };

    // Set up the subscription
    unsubscribeRef.current = subscribeToBusinessTypesList(
      handleBusinessTypesListUpdate,
      handleError
    );

    // Cleanup function
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      if (selectedBusinessTypeUnsubscribeRef.current) {
        selectedBusinessTypeUnsubscribeRef.current();
        selectedBusinessTypeUnsubscribeRef.current = null;
      }
    };
  }, []); // Empty dependency array since we want to set up subscription once and use ref for selectedBusinessType

  return {
    businessTypes,
    loading,
    error,
    selectedBusinessType,
    selectedBusinessTypeLoading,
    setSelectedBusinessType,
    refetch,
  };
};

export default useBusinessTypes;
